<script setup lang="ts">
/**
 * Pagina de captura "veja gratis algumas previas" — substitui o formulario do
 * Campaigner. O e-mail entra no funil do Workflow (ver bella/.claude/docs/
 * workflow.md), que manda a sequencia de e-mails e, no fim, inscreve na
 * newsletter.
 *
 * Layout proprio, de uma tela so', sem cabecalho e rodape do site — e' o
 * desenho entregue pelos designers. Por isso o aviso de maiores de 18, que
 * normalmente vem do layout padrao, e' incluido aqui: quem chega por anuncio
 * cai direto nesta pagina, que abre com foto explicita.
 */
import tradutor from "@/tradutors/previasgratis";
import ModalLegalWarning from "@/components/Modais/ModalLegalWarning.vue";
import heroImage from "@/assets/img/previas-gratis/hero-marcela-e-mel.jpg";
import logoPt from "@/assets/img/logo_BDS_horizontal.webp";
import logoEn from "@/assets/img/logo_BDS_horizontal_EN.png";

definePageMeta({
  layout: false,
  middleware: ["not-found-lang"],
});

useAdultRating();

const route = useRoute();
const ingles = computed(() => route.params.language === "en");
const t = computed(() => (ingles.value ? tradutor.ingles : tradutor.portugues));

/*
 * Um funil por idioma, como eram os dois formularios do Campaigner. Os
 * identificadores sao os gatilhos cadastrados em Admin > Workflow > Funil, e
 * cada funil precisa estar com "aceita entrada pela pagina publica" ligado.
 */
const GATILHOS: Record<string, string> = {
  "pt-br": "previas-gratis",
  en: "previas-gratis-en",
};

useHead(() => ({
  title: t.value.pageTitle,
  htmlAttrs: { lang: ingles.value ? "en" : "pt-BR" },
  bodyAttrs: { class: "pagina-previas-gratis" },
  meta: [
    { name: "description", content: t.value.pageDescription },
    { name: "theme-color", content: "#f4f1ec" },
  ],
  link: [
    { rel: "preconnect", href: "https://fonts.googleapis.com" },
    { rel: "preconnect", href: "https://fonts.gstatic.com", crossorigin: "" },
    {
      rel: "stylesheet",
      href: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Playfair+Display:ital,wght@0,600;0,700;1,600;1,700&display=swap",
    },
  ],
}));

const { submit } = useWorkflowEntry();

const email = ref("");
const website = ref(""); // armadilha para robo — ver o campo escondido no template
const estado = ref<"" | "invalido" | "valido">("");
const mensagem = ref("");
const tipoMensagem = ref<"" | "error" | "success">("");
const enviando = ref(false);
const enviado = ref(false);
const toast = ref("");
const campoEmail = ref<HTMLInputElement | null>(null);
let timerToast: ReturnType<typeof setTimeout> | null = null;

function mostrarToast(texto: string, duracao = 2600) {
  toast.value = texto;
  if (timerToast) clearTimeout(timerToast);
  timerToast = setTimeout(() => (toast.value = ""), duracao);
}

function emailValido(valor: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valor);
}

async function enviar() {
  const valor = email.value.trim();

  if (!emailValido(valor)) {
    estado.value = "invalido";
    mensagem.value = t.value.invalidEmail;
    tipoMensagem.value = "error";
    campoEmail.value?.focus();
    return;
  }

  if (enviando.value) return;
  enviando.value = true;

  const idioma = ingles.value ? "en" : "pt-br";
  const r = await submit({
    gatilho: GATILHOS[idioma],
    email: valor,
    locale: idioma,
    website: website.value,
  });

  enviando.value = false;

  if (r.ok) {
    estado.value = "valido";
    mensagem.value = t.value.success;
    tipoMensagem.value = "success";
    enviado.value = true;
    mostrarToast(t.value.toastSuccess);
    return;
  }

  estado.value = r.invalid ? "invalido" : "";
  mensagem.value = r.invalid ? t.value.invalidEmail : t.value.failure;
  tipoMensagem.value = "error";
}

function aoDigitar() {
  if (estado.value === "invalido") {
    estado.value = "";
    mensagem.value = "";
    tipoMensagem.value = "";
  }
}

// O play nao toca video nenhum: a previa chega por e-mail. Ele leva a pessoa
// ao campo, que e' o unico caminho para ver o ensaio.
function aoClicarPlay() {
  if (enviado.value) {
    mostrarToast(t.value.success, 3200);
    return;
  }
  campoEmail.value?.focus();
  mostrarToast(t.value.playHint, 2200);
}

onBeforeUnmount(() => {
  if (timerToast) clearTimeout(timerToast);
});
</script>

<template>
  <div class="site-shell">
    <ModalLegalWarning />

    <header class="topbar" :aria-label="ingles ? 'Main header' : 'Cabeçalho principal'">
      <NuxtLink
        class="wordmark"
        :to="{ name: 'language', params: { language: route.params.language } }"
        :aria-label="t.logoAlt"
      >
        <img class="logo-image" :src="ingles ? logoEn : logoPt" :alt="t.logoAlt" />
      </NuxtLink>
    </header>

    <main class="landing">
      <section class="visual-panel" :aria-label="t.imageAlt">
        <div class="image-frame">
          <img :src="heroImage" :alt="t.imageAlt" />
          <div class="image-overlay"></div>
          <span class="image-label">{{ t.imageLabel }}</span>
          <button class="play-button" type="button" :aria-label="t.playLabel" @click="aoClicarPlay">
            <span class="play-triangle" aria-hidden="true"></span>
          </button>
          <span class="image-caption">Marcela <i>&amp;</i> Mel</span>
        </div>
        <div class="visual-footer">
          <span>{{ t.adultsOnly }}</span>
          <span class="footer-line"></span>
        </div>
      </section>

      <section class="content-panel" aria-labelledby="main-title">
        <div class="eyebrow"><span class="eyebrow-rule"></span> {{ t.eyebrow }}</div>
        <div class="content-copy">
          <h1 id="main-title">{{ t.title1 }}<br /><em>{{ t.title2 }}</em></h1>
          <p class="intro" v-html="t.intro"></p>
        </div>

        <form class="access-form" novalidate @submit.prevent="enviar">
          <label class="field-label" for="email">{{ t.fieldLabel }} <span aria-hidden="true">*</span></label>
          <div class="field-row">
            <input
              id="email"
              ref="campoEmail"
              v-model="email"
              name="email"
              type="email"
              inputmode="email"
              autocomplete="email"
              :placeholder="t.placeholder"
              aria-describedby="form-message"
              :class="{ 'is-invalid': estado === 'invalido', 'is-valid': estado === 'valido' }"
              :disabled="enviado"
              required
              @input="aoDigitar"
            />
            <button type="submit" class="submit-button" :disabled="enviando || enviado">
              {{ enviando ? t.sending : t.button }} <span aria-hidden="true">↗</span>
            </button>
          </div>

          <!-- Armadilha para robo: fora da tela, fora do Tab, sem autocompletar.
               Gente nao preenche; preenchido, o backend finge sucesso. -->
          <div class="honeypot" aria-hidden="true">
            <label for="website">Website</label>
            <input id="website" v-model="website" name="website" type="text" tabindex="-1" autocomplete="off" />
          </div>

          <p class="consent">{{ t.consent }}</p>
          <p id="form-message" class="form-message" :class="tipoMensagem" role="status" aria-live="polite">
            {{ mensagem }}
          </p>
        </form>

        <div class="content-footer">
          <span>{{ t.footerLeft }}</span>
          <span>{{ t.footerRight }}</span>
        </div>
      </section>
    </main>

    <div class="toast" :class="{ show: toast }" role="status" aria-live="polite">{{ toast }}</div>
  </div>
</template>

<style>
/* Tela unica, sem rolagem — o corpo da pagina nao pertence ao componente. */
body.pagina-previas-gratis {
  margin: 0;
  background: #f4f1ec;
  overflow: hidden;
}
</style>

<style scoped>
.site-shell {
  --paper: #f4f1ec;
  --ink: #151515;
  --muted: #77716b;
  --line: rgba(21, 21, 21, 0.18);
  --red: #b1131d;
  --green: #4f9a62;
  --serif: "Playfair Display", Georgia, serif;
  --sans: "DM Sans", Arial, sans-serif;

  position: relative;
  height: 100svh;
  min-height: 580px;
  overflow: hidden;
  background: var(--paper);
  color: var(--ink);
}

/* O app aplica Exo-2 em `*`; aqui vale a tipografia da pagina. */
.site-shell,
.site-shell * {
  box-sizing: border-box;
  font-family: var(--sans);
}

.site-shell button,
.site-shell input {
  font: inherit;
}

.site-shell button {
  cursor: pointer;
}

.topbar {
  position: fixed;
  z-index: 10;
  top: 0;
  right: 0;
  left: 0;
  height: 94px;
  padding: 18px clamp(20px, 4.3vw, 70px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(244, 241, 236, 0.94);
  border-bottom: 1px solid var(--line);
  backdrop-filter: blur(14px);
}

.wordmark {
  display: inline-flex;
  align-items: center;
  color: var(--ink);
  text-decoration: none;
}

/* A logo do site tem fundo branco; multiply some com ele sobre o papel. */
.logo-image {
  display: block;
  width: clamp(270px, 31vw, 405px);
  height: auto;
  mix-blend-mode: multiply;
}

.landing {
  height: 100%;
  padding: 124px clamp(20px, 4.3vw, 70px) 26px;
  display: grid;
  grid-template-columns: minmax(0, 1.23fr) minmax(355px, 0.77fr);
  gap: clamp(30px, 5vw, 92px);
}

.visual-panel {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.image-frame {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #d4c9be;
}

.image-frame img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center 54%;
  filter: saturate(0.82) contrast(1.05);
  transition: transform 850ms cubic-bezier(0.2, 0.7, 0.2, 1), filter 450ms ease;
}

.image-frame:hover img {
  transform: scale(1.025);
  filter: saturate(0.95) contrast(1.06);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.25), transparent 30%, transparent 67%, rgba(0, 0, 0, 0.4));
  pointer-events: none;
}

.image-label,
.image-caption {
  position: absolute;
  color: #fff;
  z-index: 1;
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.25);
}

.image-label {
  top: 18px;
  left: 19px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.17em;
}

.site-shell .image-caption {
  right: 20px;
  bottom: 17px;
  font-family: var(--serif);
  font-size: clamp(18px, 2.1vw, 29px);
  font-style: italic;
}

.image-caption i {
  font-size: 0.55em;
  font-style: normal;
  vertical-align: middle;
}

.play-button {
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 50%;
  width: 84px;
  height: 84px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.12);
  transform: translate(-50%, -50%);
  transition: background 220ms ease, transform 220ms ease, border-color 220ms ease;
}

.play-button:hover {
  background: rgba(177, 19, 29, 0.86);
  border-color: var(--red);
  transform: translate(-50%, -50%) scale(1.06);
}

.play-triangle {
  width: 0;
  height: 0;
  margin-left: 5px;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 15px solid #fff;
}

.visual-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 24px;
  color: var(--muted);
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.15em;
}

.footer-line {
  height: 1px;
  flex: 1;
  background: var(--line);
}

.content-panel {
  min-width: 0;
  padding: 28px 0 0;
  display: flex;
  flex-direction: column;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 9px;
  color: var(--red);
  font-size: 9px;
  letter-spacing: 0.2em;
  font-weight: 700;
}

.eyebrow-rule {
  width: 30px;
  height: 1px;
  background: var(--red);
}

/* Margem automatica so' em cima: texto e formulario andam juntos. Com
   `auto` embaixo tambem, a sobra de altura da coluna abria um vao entre o
   paragrafo e o campo de e-mail. */
.content-copy {
  margin: auto 0 0;
  padding: 5vh 0 0;
}

.site-shell h1 {
  max-width: 500px;
  margin: 0 0 23px;
  font-family: var(--serif);
  font-size: clamp(45px, 5.55vw, 82px);
  line-height: 0.93;
  letter-spacing: -0.075em;
  font-weight: 600;
  color: var(--ink);
}

.site-shell h1 em {
  font-family: var(--serif);
  color: var(--red);
  font-style: italic;
}

.intro {
  max-width: 435px;
  margin: 0;
  color: #3e3a36;
  font-size: clamp(13px, 1.15vw, 16px);
  line-height: 1.52;
}

.intro :deep(strong) {
  color: var(--ink);
  font-weight: 700;
}

.access-form {
  width: min(100%, 460px);
  margin-top: 3.5vh;
}

.field-label {
  display: block;
  margin-bottom: 9px;
  color: var(--ink);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.field-label span {
  color: var(--green);
  font-size: 16px;
  vertical-align: -2px;
}

.field-row {
  display: flex;
  gap: 9px;
}

.field-row input {
  min-width: 0;
  flex: 1;
  height: 49px;
  padding: 0 15px;
  border: 1px solid #b9b2aa;
  border-radius: 0;
  outline: 0;
  background: rgba(255, 255, 255, 0.55);
  color: var(--ink);
  font-size: 13px;
  transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.field-row input::placeholder {
  color: #9a948e;
}

.field-row input:focus {
  border-color: var(--ink);
  background: #fff;
  box-shadow: 4px 4px 0 rgba(177, 19, 29, 0.14);
}

.field-row input.is-invalid {
  border-color: var(--red);
  box-shadow: 4px 4px 0 rgba(177, 19, 29, 0.13);
}

.field-row input.is-valid {
  border-color: var(--green);
}

.submit-button {
  height: 49px;
  padding: 0 18px;
  border: 1px solid var(--red);
  border-radius: 0;
  background: var(--red);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  white-space: nowrap;
  transition: background 180ms ease, color 180ms ease, transform 180ms ease;
}

.submit-button:hover:not(:disabled) {
  background: transparent;
  color: var(--red);
  transform: translateY(-2px);
}

.submit-button:disabled {
  opacity: 0.7;
  cursor: default;
}

.submit-button span {
  margin-left: 8px;
  font-size: 16px;
}

.honeypot {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.consent {
  max-width: 440px;
  margin: 12px 0 0;
  color: #7c756e;
  font-size: 10px;
  line-height: 1.45;
}

.form-message {
  min-height: 15px;
  margin: 7px 0 0;
  font-size: 10px;
  font-weight: 700;
}

.form-message.error {
  color: var(--red);
}

.form-message.success {
  color: var(--green);
}

.content-footer {
  margin-top: auto;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid var(--line);
  color: var(--muted);
  font-size: 8px;
  letter-spacing: 0.15em;
  font-weight: 700;
}

.content-footer span:last-child {
  color: var(--ink);
}

.toast {
  position: fixed;
  z-index: 20;
  right: 24px;
  bottom: 22px;
  padding: 13px 16px;
  background: var(--ink);
  color: #fff;
  font-size: 11px;
  letter-spacing: 0.04em;
  opacity: 0;
  transform: translateY(14px);
  pointer-events: none;
  transition: opacity 220ms ease, transform 220ms ease;
}

.toast.show {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 900px) {
  .landing {
    grid-template-columns: minmax(0, 1fr) minmax(305px, 0.82fr);
    gap: 28px;
  }

  .content-copy {
    padding: 4vh 0 0;
  }

  .site-shell h1 {
    font-size: clamp(42px, 7vw, 65px);
  }
}

@media (max-width: 680px) {
  .site-shell {
    min-height: 620px;
  }

  .topbar {
    height: 76px;
    padding: 14px 18px;
    justify-content: center;
  }

  .logo-image {
    width: min(292px, 82vw);
  }

  .landing {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 92px 16px 15px;
  }

  .visual-panel {
    flex: 0 0 37svh;
    min-height: 220px;
  }

  .image-label {
    top: 12px;
    left: 14px;
    font-size: 7px;
  }

  .site-shell .image-caption {
    right: 14px;
    bottom: 12px;
    font-size: 20px;
  }

  .play-button {
    width: 64px;
    height: 64px;
  }

  .play-triangle {
    border-top-width: 8px;
    border-bottom-width: 8px;
    border-left-width: 12px;
  }

  .visual-footer {
    display: none;
  }

  .content-panel {
    align-items: center;
    padding: 18px 3px 0;
    flex: 1;
    min-height: 0;
    text-align: center;
  }

  .eyebrow {
    justify-content: center;
    width: 100%;
    font-size: 8px;
  }

  .content-copy {
    padding: 1.7svh 0 1.6svh;
    margin: 0;
  }

  .site-shell h1 {
    margin-bottom: 10px;
    font-size: clamp(36px, 11vw, 52px);
    line-height: 0.9;
  }

  .intro {
    max-width: 100%;
    font-size: 11px;
    line-height: 1.33;
  }

  .access-form {
    width: 100%;
    margin-top: 0;
  }

  .field-label {
    margin-bottom: 6px;
    font-size: 10px;
  }

  .field-label span {
    font-size: 14px;
  }

  .field-row {
    gap: 7px;
  }

  .field-row input,
  .submit-button {
    height: 43px;
  }

  .field-row input {
    padding: 0 11px;
    font-size: 11px;
  }

  .submit-button {
    padding: 0 13px;
    font-size: 10px;
  }

  .submit-button span {
    margin-left: 5px;
  }

  .consent {
    margin-top: 7px;
    font-size: 8px;
    line-height: 1.28;
  }

  .form-message {
    min-height: 12px;
    margin-top: 4px;
    font-size: 9px;
  }

  .content-footer {
    width: 100%;
    justify-content: center;
    gap: 26px;
    padding-top: 9px;
    font-size: 7px;
  }

  /* No celular o aviso desce de cima, logo abaixo da logo. Embaixo ele
     ficava sob o polegar e o teclado, e brigava com a barra do navegador. */
  .toast {
    top: 88px;
    right: 16px;
    bottom: auto;
    left: 16px;
    text-align: center;
    transform: translateY(-14px);
  }

  .toast.show {
    transform: translateY(0);
  }
}

@media (max-height: 690px) and (min-width: 681px) {
  .topbar {
    height: 78px;
    padding-top: 13px;
    padding-bottom: 13px;
  }

  .landing {
    padding-top: 101px;
    padding-bottom: 14px;
  }

  .content-copy {
    padding-top: 2vh;
    padding-bottom: 0;
  }

  .access-form {
    margin-top: 2.5vh;
  }

  .site-shell h1 {
    font-size: clamp(43px, 5vw, 62px);
    margin-bottom: 15px;
  }

  .intro {
    font-size: 12px;
  }

  .visual-footer {
    height: 17px;
  }
}
</style>
